import type { DebugStats } from "./player-types";

interface DebugOverlayConfig {
  getVideo: () => HTMLVideoElement | undefined;
  getLiveEdge?: () => number | null;
  getParentElement: () => Element;
}

/**
 * Debug overlay for HLS player stats.
 * Renders outside shadow DOM to ensure visibility in popovers.
 */
export class DebugOverlay {
  private container: HTMLDivElement | null = null;
  private config: DebugOverlayConfig;

  constructor(config: DebugOverlayConfig) {
    this.config = config;
  }

  render(stats: DebugStats, isNativeHls: boolean): void {
    const video = this.config.getVideo();
    if (!video) return;

    const targetParent = this.config.getParentElement();

    // If overlay exists but in wrong parent, move it
    if (this.container && this.container.parentElement !== targetParent) {
      this.container.remove();
      targetParent.appendChild(this.container);
    }

    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "cdn-player-debug-overlay";
      targetParent.appendChild(this.container);
    }

    if (isNativeHls) {
      this.renderSafariOverlay(stats, video);
    } else {
      this.renderHlsJsOverlay(stats, video);
    }
  }

  remove(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  private renderSafariOverlay(stats: DebugStats, video: HTMLVideoElement): void {
    if (!this.container) return;

    const liveEdge = this.config.getLiveEdge?.() ?? null;
    const currentTime = video.currentTime;
    const liveDrift = liveEdge !== null ? (liveEdge - currentTime).toFixed(1) : "N/A";
    const playbackRate = video.playbackRate.toFixed(1);
    const { droppedFrames, stallCount } = stats;

    this.container.innerHTML = `
      ${this.getBaseStyles()}
      <div class="debug-section">
        <span class="debug-label">Mode</span>
        <span class="debug-value">Native HLS (Safari)</span>
      </div>
      <div class="debug-section">
        <span class="debug-label">Buffer</span>
        <div class="debug-buffer-wrapper">
          <canvas class="debug-buffer-canvas" width="400" height="16"></canvas>
        </div>
      </div>
      <div class="debug-section">
        <span class="debug-label">Live Drift</span>
        <span class="debug-value">${liveDrift}s</span>
        <span class="debug-label" style="margin-left: 12px;">Rate</span>
        <span class="debug-value">${playbackRate}x</span>
      </div>
      <div class="debug-section debug-stats-row">
        <span>Drop: ${droppedFrames}</span>
        ${stallCount ? `<span>Stalls: ${stallCount}</span>` : ""}
      </div>
    `;

    this.updateBufferCanvas(video);
  }

  private renderHlsJsOverlay(stats: DebugStats, video: HTMLVideoElement): void {
    if (!this.container) return;

    const { droppedFrames, bandwidthEstimate, bufferStalledErrors, fragLoadErrors, currentLevel, levels } = stats;

    const currentLevelBitrate =
      currentLevel >= 0 && levels[currentLevel] ? Math.round(levels[currentLevel].bitrate / 1000) : 0;

    const levelsHtml =
      levels.length > 0
        ? levels
            .map(
              (level) => `
          <span class="debug-level ${level.index === currentLevel ? "active" : ""}">
            ${level.index}:${Math.round(level.bitrate / 1000)}Kbps${level.height > 0 ? `@${level.height}p` : ""}
          </span>
        `,
            )
            .join("")
        : `<span class="debug-level">N/A</span>`;

    this.container.innerHTML = `
      ${this.getBaseStyles()}
      <div class="debug-section">
        <span class="debug-label">Buffer</span>
        <div class="debug-buffer-wrapper">
          <canvas class="debug-buffer-canvas" width="400" height="16"></canvas>
        </div>
      </div>
      <div class="debug-section">
        <span class="debug-label">Levels</span>
        <div class="debug-levels">${levelsHtml}</div>
      </div>
      <div class="debug-bitrate-section">
        <div class="debug-bitrate-label">
          Bitrate<span class="debug-bitrate-value">
            <span class="debug-bitrate-current">${currentLevelBitrate} Kbps</span> | Bandwidth:
            <span class="debug-bitrate-bandwidth">${Math.round(bandwidthEstimate)} Kbps</span>
          </span>
        </div>
        <canvas class="debug-bitrate-canvas" width="400" height="150"></canvas>
      </div>
      <div class="debug-section debug-stats-row">
        <span>Drop: ${droppedFrames}</span>
        <span>Stalls: ${bufferStalledErrors}</span>
        <span>Err: ${fragLoadErrors}</span>
      </div>
    `;

    this.updateBufferCanvas(video);
    this.updateBitrateCanvas(stats);
  }

  private updateBufferCanvas(video: HTMLVideoElement): void {
    const canvas = this.container?.querySelector(".debug-buffer-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const duration = video.duration || 0;
    const currentTime = video.currentTime;
    const bufferRanges = this.getBufferRanges(video);

    const width = canvas.width;
    const height = canvas.height;
    const seekableEnd = duration > 0 ? duration : currentTime + 30;

    ctx.fillStyle = "#222";
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#666";
    for (const range of bufferRanges) {
      const start = (range.start / seekableEnd) * width;
      const end = (range.end / seekableEnd) * width;
      ctx.fillRect(start, 2, Math.max(2, end - start), height - 4);
    }

    ctx.fillStyle = "#4af";
    const posX = (currentTime / seekableEnd) * width;
    ctx.fillRect(posX - 1, 0, 2, height);
  }

  private updateBitrateCanvas(stats: DebugStats): void {
    const canvas = this.container?.querySelector(".debug-bitrate-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const history = stats.bitrateHistory;
    const levels = stats.levels;

    ctx.fillStyle = "#222";
    ctx.fillRect(0, 0, width, height);

    if (history.length < 2) return;

    const maxLevelBitrate = Math.max(...levels.map((l) => l.bitrate / 1000), 1);
    const maxEwma = Math.max(...history.map((h) => h.ewma), 1);
    const maxBitrate = Math.max(maxLevelBitrate, maxEwma) * 1.1;

    const timeRange = 60;
    const lastPoint = history[history.length - 1];
    if (!lastPoint) return;
    const latestTime = lastPoint.time;
    const startTime = Math.max(0, latestTime - timeRange);

    // Draw bitrate line (red, step function)
    ctx.strokeStyle = "#f44";
    ctx.lineWidth = 2;
    ctx.beginPath();
    let lastY = height;
    for (let i = 0; i < history.length; i++) {
      const point = history[i];
      if (!point || point.time < startTime) continue;

      const x = ((point.time - startTime) / timeRange) * width;
      const y = height - (point.bitrate / maxBitrate) * height;

      const prevPoint = history[i - 1];
      if (i === 0 || !prevPoint || prevPoint.time < startTime) {
        ctx.moveTo(x, lastY);
      }
      ctx.lineTo(x, lastY);
      ctx.lineTo(x, y);
      lastY = y;
    }
    ctx.lineTo(width, lastY);
    ctx.stroke();

    // Draw bandwidth EWMA line (yellow)
    ctx.strokeStyle = "#fd0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    let started = false;
    for (const point of history) {
      if (point.time < startTime) continue;

      const x = ((point.time - startTime) / timeRange) * width;
      const y = height - (point.ewma / maxBitrate) * height;

      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }

  private getBufferRanges(video: HTMLVideoElement): { start: number; end: number }[] {
    const buffered = video.buffered;
    const ranges: { start: number; end: number }[] = [];
    for (let i = 0; i < buffered.length; i++) {
      ranges.push({ start: buffered.start(i), end: buffered.end(i) });
    }
    return ranges;
  }

  private getBaseStyles(): string {
    return `
      <style>
        #cdn-player-debug-overlay {
          box-sizing: border-box;
          position: fixed;
          top: 0px;
          left: 0px;
          z-index: 100000;
          background: rgba(0, 0, 0, 0.85);
          color: #fff;
          font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, monospace;
          font-size: 11px;
          padding: 12px;
          border-radius: 8px;
          pointer-events: none;
          min-width: 300px;
        }
        #cdn-player-debug-overlay .debug-section {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        #cdn-player-debug-overlay .debug-label {
          color: #888;
          text-transform: uppercase;
          font-size: 10px;
          flex-shrink: 0;
        }
        #cdn-player-debug-overlay .debug-value {
          color: #4af;
        }
        #cdn-player-debug-overlay .debug-buffer-wrapper {
          flex: 1;
          min-width: 0;
        }
        #cdn-player-debug-overlay .debug-buffer-canvas {
          width: 100%;
          height: 16px;
          background: #222;
          display: block;
        }
        #cdn-player-debug-overlay .debug-levels {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        #cdn-player-debug-overlay .debug-level {
          background: #333;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
        }
        #cdn-player-debug-overlay .debug-level.active {
          background: #4af;
          color: #000;
        }
        #cdn-player-debug-overlay .debug-bitrate-section {
          display: block;
          margin-bottom: 6px;
        }
        #cdn-player-debug-overlay .debug-bitrate-label {
          color: #888;
          text-transform: uppercase;
          font-size: 10px;
          margin-bottom: 4px;
        }
        #cdn-player-debug-overlay .debug-bitrate-value {
          margin-left: 8px;
        }
        #cdn-player-debug-overlay .debug-bitrate-current {
          color: #f44;
        }
        #cdn-player-debug-overlay .debug-bitrate-bandwidth {
          color: #fd0;
        }
        #cdn-player-debug-overlay .debug-bitrate-canvas {
          width: 100%;
          height: 150px;
          background: #1a1a1a;
          display: block;
        }
        #cdn-player-debug-overlay .debug-stats-row {
          display: flex;
          gap: 12px;
          color: #aaa;
        }
      </style>
    `;
  }
}
